/**
 * DOES THIS TECHNICIAN WORK THE SAME HOURS EVERY DAY?
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS
 * ═══════════════════════════════════════════════════════════════════════════
 * Shop Profile's roster card summarises a week as
 * `Typical 7:30 AM - 4:00 PM`, from `deriveLegacyShiftEnvelope`'s weekday
 * MODE. For Joe D — Mon/Tue 07:30–22:00, Wed–Fri 07:30–16:00 — three 16:00
 * closes outvote two 22:00 closes, so 16:00 is the mode and the card is
 * telling the truth.
 *
 * It is still misread. An operator who has just changed Tuesday to 22:00
 * comes back to the overview, sees four o'clock, and concludes the edit did
 * not save. The sentence is accurate and the impression is false, because
 * nothing in it admits there is more than one answer.
 *
 * So the card gains a qualifier, and this decides when it has earned one.
 * `deriveLegacyShiftEnvelope` is NOT changed: the mode is the right number
 * for a summary, and a summary that says it is one is honest.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY IT DOES NOT REUSE THE ENVELOPE
 * ═══════════════════════════════════════════════════════════════════════════
 * `deriveLegacyShiftEnvelope` identifies weekdays by NUMERIC key
 * (`weekdaySet.has(Number(day))`), so a day-name-keyed map misses that branch
 * entirely. `technicians.hours` is written index-keyed by `TechnicianForm`
 * and day-name-keyed by the commit wizard and several edge writers — the very
 * split that had the Dayboard drawing the wrong day.
 *
 * This reads BOTH shapes, with the same index-first precedence
 * `getTechDayHours` uses, so one day is counted once and the two readers can
 * never disagree about which entry a day has.
 */

/** The hours map as it is actually stored: numeric OR day-name keys. */
export type TechnicianHoursMap = Record<
  string | number,
  { open?: string | null; close?: string | null } | null | undefined
>;

const DAY_NAMES = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
] as const;

/**
 * The distinct open/close pairs this technician actually works.
 *
 * A day is WORKING only when it has both an open and a close — the same test
 * `getTechDayHours` applies before returning. Everything else (absent, empty
 * strings, half-written) is "not working", which is not a different shift and
 * must not read as one: otherwise every part-timer's card would claim an
 * irregular schedule.
 */
export function distinctWorkingShifts(
  hours: TechnicianHoursMap | null | undefined,
): string[] {
  if (!hours) return [];

  const shifts = new Set<string>();
  for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
    // Index first, then the day name — `getTechDayHours`'s precedence. A day
    // written under both keys is ONE day, and reading both would invent a
    // variation out of a single entry.
    const entry = hours[String(dayIndex)] ?? hours[DAY_NAMES[dayIndex]];
    const open = entry?.open?.trim();
    const close = entry?.close?.trim();
    if (open && close) shifts.add(`${open}-${close}`);
  }
  return [...shifts];
}

/**
 * Has this technician got more than one working-day shape in their week?
 *
 * False for an empty schedule and for a single working day: "nobody has said"
 * and "one day" are both weeks with nothing extra to report, and a qualifier
 * on either would be noise attached to a card that is already right.
 */
export function hasVariableWorkingHours(
  hours: TechnicianHoursMap | null | undefined,
): boolean {
  return distinctWorkingShifts(hours).length > 1;
}
